import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import LanguageSwitcher from "./LanguageSwitcher";
import FilloutButton from "./FilloutButton";
import { supportedLanguages, type SupportedLanguage } from "@/lib/i18n";
import { generateLocalizedUrl } from "@/lib/slugUtils";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useScrollDirection } from "@/hooks/useScrollDirection";
import { useIsMobile } from "@/hooks/use-mobile";

const Header = () => {
  const { t, ready } = useTranslation('common');
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isHeaderVisible, isBannerVisible } = useScrollDirection();
  const isMobile = useIsMobile();

  if (!ready) {
    return (
      <header className="w-full bg-gradient-hero border-b border-golden-primary/20 h-16 flex items-center">
        <div className="container mx-auto px-4">
          <div className="animate-pulse flex space-x-4">
            <div className="rounded-md bg-primary-foreground/20 h-8 w-20"></div>
            <div className="flex-1 space-y-2 py-1">
              <div className="h-4 bg-primary-foreground/20 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const mobileMenuRef = useClickOutside<HTMLDivElement>(closeMobileMenu);

  return (
    <>
      {/* Main Header */}
      <header className={`w-full bg-gradient-hero border-b border-golden-primary/20 fixed top-0 left-0 right-0 z-[100] transition-transform duration-300 ${!isHeaderVisible ? '-translate-y-full' : 'translate-y-0'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={`/${currentLang}/`} className="flex items-center space-x-3 group">
              <img 
                src="/images/logo.png" 
                alt="Price Forms Logo" 
                className="h-12 w-auto group-hover:scale-105 transition-elegant"
              />
            </Link>
            
            <nav className="hidden md:flex items-center space-x-8">
              <Link to={`/${currentLang}/`} className="text-primary-foreground hover:text-accent transition-elegant relative group">
                <span>{t('navigation.home')}</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent group-hover:w-full transition-elegant"></div>
              </Link>
              <Link to={generateLocalizedUrl('about', currentLang)} className="text-primary-foreground hover:text-accent transition-elegant relative group">
                <span>{t('navigation.about')}</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent group-hover:w-full transition-elegant"></div>
              </Link>
              <Link to={generateLocalizedUrl('services', currentLang)} className="text-primary-foreground hover:text-accent transition-elegant relative group">
                <span>{t('navigation.services')}</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent group-hover:w-full transition-elegant"></div>
              </Link>
              <Link to={generateLocalizedUrl('portfolio', currentLang)} className="text-primary-foreground hover:text-accent transition-elegant relative group">
                <span>{t('navigation.portfolio')}</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent group-hover:w-full transition-elegant"></div>
              </Link>
              <Link to={generateLocalizedUrl('contact', currentLang)} className="text-primary-foreground hover:text-accent transition-elegant relative group">
                <span>{t('navigation.contact')}</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent group-hover:w-full transition-elegant"></div>
              </Link>
            </nav>
            
            <div className="flex items-center space-x-4">
              <div className="hidden md:block">
                <LanguageSwitcher />
              </div>
              <FilloutButton variant="secondary" className="hidden md:inline-flex">
                {t('buttons.scheduleCall')}
              </FilloutButton>
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden text-primary-foreground/80 hover:text-accent hover:bg-primary-foreground/10 transition-elegant"
                onClick={toggleMobileMenu}
                aria-label={isMobileMenuOpen ? t('buttons.closeMenu') : t('buttons.openMenu')}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div ref={mobileMenuRef} className="md:hidden absolute top-full left-0 right-0 bg-gradient-hero border-b border-golden-primary/20 z-50 shadow-lg">
            <nav className="container mx-auto px-4 py-4 space-y-4">
              <Link 
                to={`/${currentLang}/`} 
                className="block text-primary-foreground hover:text-accent transition-elegant py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navigation.home')}
              </Link>
              <Link 
                to={generateLocalizedUrl('about', currentLang)} 
                className="block text-primary-foreground hover:text-accent transition-elegant py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navigation.about')}
              </Link>
              <Link 
                to={generateLocalizedUrl('services', currentLang)} 
                className="block text-primary-foreground hover:text-accent transition-elegant py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navigation.services')}
              </Link>
              <Link 
                to={generateLocalizedUrl('portfolio', currentLang)} 
                className="block text-primary-foreground hover:text-accent transition-elegant py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navigation.portfolio')}
              </Link>
              <Link 
                to={generateLocalizedUrl('contact', currentLang)} 
                className="block text-primary-foreground hover:text-accent transition-elegant py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navigation.contact')}
              </Link>
              
              <div className="border-t border-primary-foreground/20 pt-4">
                <LanguageSwitcher onLanguageChange={() => setIsMobileMenuOpen(false)} />
              </div>
              
              <FilloutButton 
                variant="secondary" 
                className="w-full"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('buttons.scheduleCall')}
              </FilloutButton>
            </nav>
          </div>
        )}
      </header>
      
      {/* Welcome Banner */}
      {isBannerVisible && (
        <div className={`w-full bg-accent text-center py-3 relative overflow-hidden fixed top-20 left-0 right-0 z-[90]`}>
          <div className="absolute inset-0 bg-gradient-to-r from-accent/50 via-accent to-accent/50"></div>
          <p className="text-accent-foreground text-sm font-medium relative z-10 animate-fade-in-up">
            {t('banner.welcome')}
          </p>
        </div>
      )}
      
      {/* Spacer to push content below fixed header */}
      <div className={`${isBannerVisible ? 'h-[76px]' : 'h-20'}`} />
    </>
  );
};

export default Header;